import React, { useState, useEffect, useRef } from 'react';
import { postAPI, postAPIForm } from '../../../../app/api';
import editIcon from "../../../../theme/assets/images/icon/edit-icon.svg"
import deleteIcon from "../../../../theme/assets/images/icon/delete-icon.svg"
import Swal from 'sweetalert2';

export default function PopupSectionsManager() {
    const [popupData, setPopupData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        image: null,
        frequency: 0,
        is_active: 1
    });

    // Frequency options
    const frequencyOptions = [
        { value: 0, label: 'Every Visit' },
        { value: 1, label: 'Once Per Session' },
        { value: 2, label: 'Once Per Day' },
        { value: 3, label: 'Once Per Week' },
        { value: 4, label: 'Once Only' }
    ];

    // Fetch popup data on mount
    const hasFetched = useRef(false);
    useEffect(() => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            fetchPopupData();
        }
    }, []);

    const fetchPopupData = async () => {
        try {
            setLoading(true);
            const response = await postAPI('popup-data/list', {});
            if (response?.status === 1 && response?.data?.length > 0) {
                // Get the first active popup for home page
                const homePopup = response.data.find(p => p.route === 'home' || p.route === 'all');
                setPopupData(homePopup || null);
            } else {
                setPopupData(null);
            }
        } catch (error) {
            console.error('Error fetching popup data:', error);
            Swal.fire('Error', 'Failed to fetch popup data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Upload image immediately
            const imgFormData = new FormData();
            imgFormData.append('baseDir', 'popup');
            imgFormData.append('filename', 'popup_image');
            imgFormData.append('original_file', file);

            try {
                const imgResponse = await postAPIForm('file/uploadFile', imgFormData);
                if (imgResponse?.status === 1) {
                    setFormData(prev => ({
                        ...prev,
                        image: imgResponse?.filepath
                    }));
                    Swal.fire('Success', 'Image uploaded successfully', 'success');
                } else {
                    Swal.fire('Error', 'Failed to upload image', 'error');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                Swal.fire('Error', 'Failed to upload image', 'error');
            }
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleEditPopup = () => {
        if (popupData) {
            setFormData({
                image: popupData.image || null,
                frequency: popupData.frequency || 0,
                is_active: popupData.is_active !== undefined ? popupData.is_active : 1
            });
            setEditingId(popupData.id);
            setShowForm(true);
        }
    };

    const handleCreateNewPopup = () => {
        setEditingId(null);
        setFormData({
            image: null,
            frequency: 0,
            is_active: 1
        });
        setShowForm(true);
    };

    const handleSavePopup = async (e) => {
        e.preventDefault();
        try {
            if (!formData.image) {
                Swal.fire('Validation', 'Please upload an image', 'warning');
                return;
            }

            const submitData = {
                image: formData.image,
                route: 'home',
                frequency: parseInt(formData.frequency),
                is_active: formData.is_active
            };

            // Use editingId if editing, OR use existing popupData.id if one exists
            // This prevents creating duplicates when a popup already exists
            if (editingId) {
                submitData.id = editingId;
            } else if (popupData?.id) {
                // If trying to create but a popup already exists, update it instead
                submitData.id = popupData.id;
            }

            const response = await postAPI('popup-data/create', submitData);
            if (response?.status === 1) {
                Swal.fire('Success', 'Popup saved successfully', 'success');
                resetForm();
                fetchPopupData();
            } else {
                Swal.fire('Error', response?.msg || 'Failed to save popup', 'error');
            }
        } catch (error) {
            console.error('Error saving popup:', error);
            Swal.fire('Error', error.message || 'Failed to save popup', 'error');
        }
    };

    const handleDeletePopup = async () => {
        if (!popupData?.id) return;

        const result = await Swal.fire({
            title: 'Confirm Delete',
            text: 'Are you sure you want to delete this popup?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const response = await postAPI('popup-data/delete', { id: popupData.id });
                if (response?.status === 1) {
                    Swal.fire('Success', 'Popup deleted successfully', 'success');
                    setPopupData(null);
                    resetForm();
                } else {
                    Swal.fire('Error', response?.msg || 'Failed to delete popup', 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'Failed to delete popup', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            image: null,
            frequency: 0,
            is_active: 1
        });
        setShowForm(false);
        setEditingId(null);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Homepage Popup Manager</h5>
                            {!showForm && (
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={popupData ? handleEditPopup : handleCreateNewPopup}
                                >
                                    {popupData ? 'Edit Popup' : 'Create Popup'}
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : showForm ? (
                                // Simplified Form
                                <form onSubmit={handleSavePopup}>
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Popup Image *</label>
                                        <div className="d-flex flex-column align-items-start gap-3">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                className="d-none"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={handleUploadClick}
                                            >
                                                <i className="fa fa-upload me-2"></i>
                                                {formData.image ? 'Change Image' : 'Upload Image'}
                                            </button>
                                            {formData.image && (
                                                <div className="mt-2">
                                                    <p className="text-muted mb-2">Preview:</p>
                                                    <img
                                                        src={formData.image}
                                                        alt="Popup Preview"
                                                        className="img-fluid rounded border"
                                                        style={{ maxHeight: '300px', maxWidth: '100%' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Display Frequency</label>
                                            <select
                                                className="form-control"
                                                name="frequency"
                                                value={formData.frequency}
                                                onChange={handleChange}
                                            >
                                                {frequencyOptions.map(freq => (
                                                    <option key={freq.value} value={freq.value}>{freq.label}</option>
                                                ))}
                                            </select>
                                            <small className="text-muted">
                                                Controls how often the popup is shown to visitors
                                            </small>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch mt-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="is_active"
                                                    name="is_active"
                                                    checked={formData.is_active === 1}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor="is_active">
                                                    {formData.is_active === 1 ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                            <small className="text-muted">
                                                Toggle to enable/disable the popup
                                            </small>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-2 mt-4">
                                        <button type="submit" className="btn btn-success">
                                            <i className="fa fa-save me-2"></i>Save Popup
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={resetForm}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : popupData ? (
                                // Display current popup
                                <div>
                                    <div className="row">
                                        <div className="col-md-8">
                                            <div className="border rounded p-3 bg-light">
                                                <h6 className="text-muted mb-3">Current Popup Image</h6>
                                                {popupData.image ? (
                                                    <img
                                                        src={popupData.image}
                                                        alt="Current Popup"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '400px' }}
                                                    />
                                                ) : (
                                                    <p className="text-muted">No image uploaded</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="border rounded p-3">
                                                <h6 className="text-muted mb-3">Settings</h6>
                                                <table className="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Status:</strong></td>
                                                            <td>
                                                                <span className={`badge ${popupData.is_active === 1 ? 'bg-success' : 'bg-secondary'}`}>
                                                                    {popupData.is_active === 1 ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Frequency:</strong></td>
                                                            <td>
                                                                {frequencyOptions.find(f => f.value === parseInt(popupData.frequency))?.label || 'Every Visit'}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                <div className="d-flex gap-2 mt-3">
                                                    <button
                                                        className="btn btn-sm btn-primary"
                                                        onClick={handleEditPopup}
                                                    >
                                                        <img src={editIcon} alt="Edit" style={{ width: '14px' }} className="me-1" />
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={handleDeletePopup}
                                                    >
                                                        <img src={deleteIcon} alt="Delete" style={{ width: '14px' }} className="me-1" />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // No popup configured
                                <div className="text-center py-5">
                                    <div className="mb-4">
                                        <i className="fa fa-image fa-4x text-muted"></i>
                                    </div>
                                    <h5 className="text-muted mb-3">No Popup Configured</h5>
                                    <p className="text-muted mb-4">
                                        Upload an image to display as a popup on the homepage
                                    </p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleCreateNewPopup}
                                    >
                                        <i className="fa fa-plus me-2"></i>Create Popup
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
